// The one place this app asks the kernel which engagement features are on.
//
// ⭐ EVERY ENGAGEMENT SCREEN GATES ON THIS HOOK, and none of them gates on anything else.
// URS_12.5 AC 2 — *"disabling a feature … hides or disables the feature in the patient's
// mobile app via the feature-gating endpoint"* — is only true if there is exactly one answer to
// "is messaging on", and it comes from the server.
//
// ⛔ DO NOT ADD A CACHE OR A CONTEXT PROVIDER HERE WITHOUT READING THIS. AC 3 of the same story
// is *"the mobile app receives flag changes promptly"*. A provider mounted once at app start
// would hold an admin's 10:00 toggle change until the next cold start. The hook refetches when
// a screen mounts and when the tab becomes visible again, which is the shape that keeps the
// promise; the payload is six small fields, so the call costs nothing worth optimising.
//
// ⚠️ FAILURE IS "EVERYTHING OFF", NOT "EVERYTHING ON". An unreachable kernel must not let the
// app offer a feature the organization has switched off — the write would be refused anyway,
// after the patient had typed.
//
// ⛔ SIGNED OUT MEANS NO CALL. Ionic keeps a page mounted after the user leaves it, so the
// screen someone signed out from keeps running this hook on the sign-in and code screens. Each
// call 401'd, the shared interceptor's refresh 401'd too (web: no cookie), and the resulting
// auth:logout wiped the in-flight login and threw the user off the code screen (prod
// 2026-10-27). The hook answers "off" without asking until someone is signed in, and asks the
// moment they are.

import { useCallback, useEffect, useState } from "react";
import useAuth from "./useAuth";
import {
  EngagementFeatures,
  NO_ENGAGEMENT_FEATURES,
  getEngagementFeatures,
} from "../services/engagement";

export interface UseEngagementFeatures {
  features: EngagementFeatures;
  /** True until the first answer arrives — screens show a skeleton, never an empty state. */
  isLoading: boolean;
  /** The patient has no organization holding their record: nothing to show, and it is not an error. */
  hasOrganization: boolean;
  /**
   * F-885 — the last call FAILED (after the shared 401 refresh-and-retry). Features stay off
   * (closed, see the header), but a screen must say so rather than look like "no organization":
   * an unanswered call is not an answer.
   */
  error: boolean;
  refresh: () => Promise<void>;
}

export const useEngagementFeatures = (): UseEngagementFeatures => {
  const [features, setFeatures] = useState<EngagementFeatures>(NO_ENGAGEMENT_FEATURES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const { isAuthenticated } = useAuth();

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setFeatures(NO_ENGAGEMENT_FEATURES);
      setError(false);
      setIsLoading(false);
      return;
    }
    try {
      setFeatures(await getEngagementFeatures());
      setError(false);
    } catch (err) {
      // Closed, not open — see the header.
      console.error("engagement: feature flags could not be read", err);
      setFeatures(NO_ENGAGEMENT_FEATURES);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
    // A patient leaves the app on a screen, an admin flips a toggle, they come back. Without
    // this listener the UI would still be offering the feature until they navigated away.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return {
    features,
    isLoading,
    hasOrganization: features.org_id !== null,
    error,
    refresh,
  };
};
